import { createFileRoute } from "@tanstack/react-router";
import { canonicalUrl, canonicalLink, geoMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { WagonCoverForm } from "@/components/WagonCoverForm";
import { DropCurtainForm } from "@/components/DropCurtainForm";
import { BoatCoverForm } from "@/components/BoatCoverForm";
import { CoolerCushionForm } from "@/components/CoolerCushionForm";
import { HuntingTarpForm } from "@/components/HuntingTarpForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/request-a-quote")({
  head: () => ({
    links: [canonicalLink("/request-a-quote")],
    meta: [
        ...geoMeta(),{ property: "og:url", content: canonicalUrl("/request-a-quote") },
      { title: "Request a Quote — Texas Made Shade" },
      {
        name: "description",
        content:
          "Request a quote for wagon covers, drop curtains, boat covers, cooler cushions & hunting tarps — serving San Antonio, Austin & all of Texas.",
      },
      { name: "geo.placename", content: "San Antonio, Austin, Seguin, Texas" },
      { name: "location", content: "San Antonio, Austin, Texas" },
      { property: "og:title", content: "Request a Quote — Texas Made Shade" },
      {
        property: "og:description",
        content:
          "All Texas Made Shade quote forms in one place — wagon covers, drop curtains, boat covers, cooler cushions, hunting tarps. San Antonio & Austin.",
      },
    ],
    scripts: pageHeadScripts({
      path: "/request-a-quote",
      name: "Request a Quote — Texas Made Shade",
      description: "Request a quote for wagon covers, drop curtains, boat covers, cooler cushions and hunting tarps — serving San Antonio, Austin and all of Texas.",
    }),
  }),
  component: RequestAQuotePage,
});

const QUOTE_OPTIONS = [
  { id: "wagon-cover", label: "Wagon Cover" },
  { id: "drop-curtain", label: "Drop Curtain" },
  { id: "boat-cover", label: "Boat Cover" },
  { id: "cooler-cushion", label: "Cooler Cushion / Gear Cover" },
  { id: "hunting-tarp", label: "Hunting Tarp / Blind Cover" },
] as const;

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  history.replaceState(null, "", `#${id}`);
}

function RequestAQuotePage() {
  return (
    <>
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
      <section className="border-b-4 border-navy-deep bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <p className="font-display text-sm uppercase tracking-[0.3em] text-navy-deep/70">
            Texas Made Shade
          </p>
          <h1 className="mt-3 font-display text-4xl uppercase text-navy-deep sm:text-5xl">
            Request a Quote
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-navy-deep/80 sm:text-lg">
            What do you need a quote for? Pick a category below to jump straight
            to the right form.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {QUOTE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => scrollToId(opt.id)}
                className="rounded-none border-2 border-navy-deep bg-cream px-5 py-3 font-display text-sm uppercase tracking-wide text-navy-deep shadow-card transition hover:bg-navy-deep hover:text-cream"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="wagon-cover" className="scroll-mt-24 border-b-2 border-navy-deep/20">
        <SectionHeading eyebrow="01" title="Wagon Cover Quote" />
        <WagonCoverForm />
      </section>

      <section id="drop-curtain" className="scroll-mt-24 border-b-2 border-navy-deep/20">
        <SectionHeading eyebrow="02" title="Drop Curtain Quote" />
        <DropCurtainForm />
      </section>

      <section id="boat-cover" className="scroll-mt-24 border-b-2 border-navy-deep/20">
        <SectionHeading eyebrow="03" title="Boat Cover Quote" />
        <BoatCoverForm />
      </section>

      <section id="cooler-cushion" className="scroll-mt-24 border-b-2 border-navy-deep/20">
        <SectionHeading eyebrow="04" title="Cooler Cushion / Gear Cover Quote" />
        <CoolerCushionForm />
      </section>

      <section id="hunting-tarp" className="scroll-mt-24">
        <SectionHeading eyebrow="05" title="Hunting Tarp / Blind Cover Quote" />
        <HuntingTarpForm />
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mx-auto max-w-6xl px-4 pt-12 sm:pt-16">
      <p className="font-display text-xs uppercase tracking-[0.3em] text-navy-deep/60">
        Cat {eyebrow}
      </p>
      <h2 className="mt-2 font-display text-3xl uppercase text-navy-deep sm:text-4xl">
        {title}
      </h2>
    </div>
  );
}
